import { useTranslation } from "react-i18next";
import React, { useState, useEffect } from "react";
import { Drawer, Space, Form, Row, Skeleton, Button, Input, Modal, Tooltip } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import PropTypes from "prop-types";

import { PaginatedTableWidget } from "misc/tables";
import { TextField } from "misc/fields";

const EditableTable = ({
    dataSource,
    columns,
    rowKey,
    form,
    extraHeader,
    onAddItem,
    onEditItem,
    onDeleteItem,
    onOpenDrawer,
    extraActions,
    isLoading,
    ...props
}) => {
    const [t] = useTranslation();

    const [drawerForm] = Form.useForm();

    const [drawerIsOpen, setDrawerIsOpen] = useState(false);
    const [drawerCompletelyLoaded, setDrawerCompletelyLoaded] = useState(false);
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);

    useEffect(() => {
        if (drawerCompletelyLoaded) {
            drawerForm.resetFields();
            onOpenDrawer && onOpenDrawer(selectedItem);
        } else setSelectedItem(null);

        // eslint-disable-next-line
    }, [drawerCompletelyLoaded]);

    useEffect(() => {
        modalIsOpen || setSelectedItem(null);
        // eslint-disable-next-line
    }, [modalIsOpen]);

    const onSave = item => {
        selectedItem ? onEditItem && onEditItem({ ...selectedItem, ...item }) : onAddItem && onAddItem(item);
        setDrawerIsOpen(false);
    };

    const onDelete = () => {
        onDeleteItem && onDeleteItem(selectedItem);
        setModalIsOpen(false);
    };

    const renderActions = (text, record) => (
        <Space>
            {onEditItem && (
                <Tooltip title={<TextField value="t_edit" />}>
                    <Button
                        icon={<FontAwesomeIcon icon={["fal", "pen-to-square"]} />}
                        onClick={() => {
                            setDrawerIsOpen(true);
                            setSelectedItem(record);
                        }}
                        disabled={!record.editable}
                    />
                </Tooltip>
            )}
            {onDeleteItem && (
                <Tooltip title={<TextField value="t_delete" />}>
                    <Button
                        icon={<FontAwesomeIcon icon={["fal", "trash"]} />}
                        onClick={() => {
                            setModalIsOpen(true);
                            setSelectedItem(record);
                        }}
                        disabled={!record.deletable}
                    />
                </Tooltip>
            )}
            {extraActions &&
                extraActions(record).map(action => (
                    <Tooltip key={action.key} title={<TextField value={action.tooltip} />}>
                        <Button {...action} />
                    </Tooltip>
                ))}
        </Space>
    );

    const tableColumns = [
        ...columns,
        ...(onEditItem || onDeleteItem || extraActions
            ? [
                  {
                      key: "actions",
                      render: renderActions,
                      width: 120
                  }
              ]
            : [])
    ];

    return (
        <Space direction="vertical" size={25} style={{ width: "100%" }}>
            <PaginatedTableWidget
                dataSource={dataSource}
                columns={tableColumns}
                rowKey={rowKey}
                isLoading={isLoading}
                extraHeader={extraHeader}
                {...props}
            />
            <Drawer
                open={drawerIsOpen}
                title={selectedItem ? <TextField value="t_edit_item" /> : <TextField value="t_add_item" />}
                onClose={() => setDrawerIsOpen(false)}
                width={500}
                afterOpenChange={visible => setDrawerCompletelyLoaded(visible)}
            >
                {drawerCompletelyLoaded ? (
                    <Form form={drawerForm} initialValues={form.initialValues(selectedItem)} layout="vertical" onFinish={onSave} hideRequiredMark>
                        {form.items}
                        <Form.Item>
                            <Button type="primary" htmlType="submit">
                                <TextField value="t_save" inline />
                            </Button>
                        </Form.Item>
                    </Form>
                ) : (
                    <Skeleton />
                )}
            </Drawer>

            <Modal
                title={<TextField value="t_delete_item_title" />}
                open={modalIsOpen}
                onCancel={() => setModalIsOpen(false)}
                onOk={onDelete}
                cancelText={<TextField value="t_cancel" />}
                okText={<TextField value="t_delete" />}
                okButtonProps={{ danger: true }}
            >
                <Space direction="vertical" size={20}>
                    <div>
                        <TextField value="t_delete_item_description" />

                        <TextField value="t_this_can_not_be_undone" />
                    </div>

                    <TextField value="t_do_you_want_to_continue" />
                </Space>
            </Modal>
        </Space>
    );
};

EditableTable.propTypes = {
    dataSource: PropTypes.arrayOf(PropTypes.object).isRequired,
    columns: PropTypes.arrayOf(PropTypes.object).isRequired,
    rowKey: PropTypes.string.isRequired,
    form: PropTypes.shape({
        initialValues: PropTypes.func.isRequired,
        items: PropTypes.arrayOf(PropTypes.node).isRequired
    }),
    onAddItem: PropTypes.func,
    onEditItem: PropTypes.func,
    onDeleteItem: PropTypes.func,
    onOpenDrawer: PropTypes.func,
    extraActions: PropTypes.func
};

export default EditableTable;
